import { ApolloError } from '@apollo/client'
import { NetworkStatus } from '@apollo/client/core/networkStatus'
import { catchError, combineLatest, filter, from, map, Observable, switchMap } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import {
  ConfigurableProductDetailsFragment,
  GetProductsByUrlKeyDocument,
} from '../../../graphql/magento'
import { FetchError } from '../../../utils/errorTools'
import { left, Result, right } from '../../../utils/result'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight } from '../../../utils/rx/operators'
import { latestAccessToken$ } from '../../auth/state'
import { fetchResponseToJsonResult, FetchResult, headers } from '../../fetch'
import { latestApolloClient$ } from '../../graphql/apollo'
import { ProductDetails } from './types'

interface SanityAllParentProductResponse {
  data: {
    allParentProduct: Array<ProductDetails>
  }
}

const createSanityParentProductQuery = (sku: string) => {
  return {
    query: `
      query {
        allParentProduct(where: {sku: {eq: "${sku}"}}, sort: {_updatedAt: DESC}) {
          sku
          shortDescription
          tagline
        }
      }
    `,
  }
}

export const latestApolloClientAndToken$ = combineLatest([
  latestApolloClient$,
  latestAccessToken$,
]).pipe(map(([client, token]) => ({ client, token })))

export const sanityParentProductQuery = (sku: string): Observable<FetchResult<ProductDetails>> =>
  fromFetch('/api/graphql', {
    body: JSON.stringify(createSanityParentProductQuery(sku)),
    method: 'POST',
    headers: {
      ...headers.contentTypeJson,
    },
  }).pipe(
    switchMap((response) => fetchResponseToJsonResult<SanityAllParentProductResponse>(response)),
    mapRight((result) => {
      const data = result.data.data.allParentProduct[0]
      return data
        ? { _tag: 'Right' as const, data }
        : {
            _tag: 'Left' as const,
            error: new FetchError('Parent product not found for SKU'),
          }
    }),
    catchError(coerceCaughtToLeft),
  )

export const configurableProductByUrlKey = (
  urlKey: string,
): Observable<Result<ConfigurableProductDetailsFragment, ApolloError | Error>> =>
  latestApolloClientAndToken$.pipe(
    switchMap(({ client, token }) =>
      from(
        client.query({
          query: GetProductsByUrlKeyDocument,
          variables: { urlKey },
          context: { ...(token && { token }) },
          fetchPolicy: 'cache-first',
        }),
      ),
    ),
    filter(
      ({ networkStatus }) =>
        networkStatus === NetworkStatus.ready || networkStatus === NetworkStatus.error,
    ),
    map(({ data, error }) => (error ? left(error) : right(data))),
    mapRight(({ data }) =>
      data?.products?.items?.[0]?.__typename === 'ConfigurableProduct'
        ? right(data.products.items[0])
        : left(new Error('Product not found')),
    ),
  )
